import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, Globe } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const DomainAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: '', api_user: '', api_key: '', username: '' });
  const [serverIp, setServerIp] = useState("Loading...");

  useEffect(() => {
    fetchAccounts();
    fetchIp();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (e) {
      toast.error("Failed to fetch accounts");
    }
  };

  const fetchIp = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/ip`, { withCredentials: true });
      setServerIp(res.data.ip);
    } catch (e) {
      setServerIp("Unknown");
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/accounts`, form, { withCredentials: true });
      toast.success("Namecheap Account added successfully");
      setShowAdd(false);
      setForm({ label: '', api_user: '', api_key: '', username: '' });
      fetchAccounts();
    } catch (e) {
      toast.error("Failed to add Namecheap account");
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm("Are you sure you want to remove this account?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/accounts/${id}`, { withCredentials: true });
      fetchAccounts();
      toast.success("Account removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove account");
    }
  };

  const testConnection = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap/accounts/${id}/test`, {}, { withCredentials: true });
      toast.success("Connection OK! Namecheap API authorized.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection Failed: Invalid Credentials or Permissions");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-black text-[#00E5FF] mb-4">NAMECHEAP ACCOUNTS</h1>
          
          <div className="bg-[#0A0B0E] border border-[#00E5FF]/30 p-6 max-w-3xl space-y-4 text-sm text-slate-300 font-mono">
            <h3 className="text-[#00E5FF] font-bold font-sans text-lg uppercase tracking-wider mb-2">How to Generate Namecheap API Credentials</h3>
            
            <div className="space-y-1">
              <p><strong className="text-white">Step 1:</strong> Log into Namecheap and go to <a href="https://apcp.namecheap.com/settings/tools/apiaccess" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">Profile &gt; Tools &gt; Namecheap API Access</a>.</p>
              <p><strong className="text-white">Step 2:</strong> Ensure the toggle at the top right is turned **ON**.</p>
              <p><strong className="text-white">Step 3:</strong> Add this exact Server IP to your Whitelisted IPs list:</p>
            </div>

            <div className="bg-black/50 border border-[#00E5FF]/30 p-4 text-center">
              <span className="text-2xl font-black text-[#00E5FF] select-all tracking-widest">{serverIp}</span>
            </div>

            <div className="pt-2">
              <p><strong className="text-white">Step 4:</strong> Copy your `APIUser`, `APIKey`, and `UserName` and paste them below.</p>
            </div>
          </div>
        </div>
        <button 
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#00E5FF] text-black px-4 py-2 font-bold hover:bg-[#00B8CC] transition-colors shrink-0"
        >
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#00E5FF]">LINK NAMECHEAP ACCOUNT</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">API User</label>
                <input required value={form.api_user} onChange={(e) => setForm({...form, api_user: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Username</label>
                <input required value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">API Key</label>
                <input required type="password" value={form.api_key} onChange={(e) => setForm({...form, api_key: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] font-mono text-sm" />
              </div>
              <button type="submit" className="w-full bg-[#00E5FF] text-black font-bold py-3 mt-4 hover:bg-[#00B8CC] transition-colors uppercase tracking-wider">
                Link Account
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map(acc => (
          <div key={acc.id} className="border border-[#00E5FF]/20 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <Globe size={24} className="text-[#00E5FF]" />
                <h3 className="text-xl font-bold">{acc.label}</h3>
              </div>
              <div className="flex gap-2">
                <button onClick={() => testConnection(acc.id)} className="text-slate-400 hover:text-[#00E5FF] transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="namecheap" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>
            <div className="space-y-2 text-sm font-mono text-slate-400 border-t border-white/10 pt-4 mt-auto">
              <p>API User: <span className="text-white">{acc.api_user}</span></p>
              <p>Username: <span className="text-white">{acc.username}</span></p>
            </div>
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">
            No Namecheap accounts linked.
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainAccounts;